/* ──────────────────────────────────────────────────────────────────────
 * Patchistry — Cursor Morph (follower)
 * Added 2026-05-19 (sensory polish phase).
 *
 * Important: this does NOT replace the existing PNG cursor system
 * (which is intentionally preserved). It adds a secondary follower —
 * a small floating dot that morphs based on context.
 *
 * Behavior:
 *   - Default: 6px brown dot trailing cursor with subtle lag
 *   - Over interactive (a, button, [data-pcr-card], [data-pcr-filter]):
 *     expands to 32px gold ring, dot shrinks
 *   - Over video / media element: hides entirely
 *   - Over patch grid: slightly larger (18px)
 *   - Disabled on touch devices + prefers-reduced-motion
 *
 * Premium reference: Bottega Veneta, Le Labo, Toteme, Jacquemus all
 * use a similar dual-cursor pattern.
 * ─────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // Skip on touch + reduced motion
  var hasTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (hasTouch || reduced) return;
  // Also skip if viewport is small (mobile UA without touch)
  if (window.innerWidth < 900) return;
  // Skip on heavy-DOM pages where mouseover.closest() is expensive
  // (builder has 250+ patches + canvas cards + filter chips — Lighthouse
  // measured 1.97s of CPU time on Build Yours alone). These pages also
  // have their own native cursor treatment, so this would be redundant.
  var path = location.pathname;
  if (path.indexOf('/pages/build-yours') === 0 ||
      path.indexOf('/pages/build-for-') === 0 ||
      path.indexOf('/products/') === 0) return;

  // ── Inject styles ───────────────────────────────────────────────────
  var css =
    '.p-cursor-dot,.p-cursor-ring{' +
      'position:fixed;top:0;left:0;pointer-events:none;z-index:99998;' +
      'will-change:transform,width,height,opacity;' +
      'border-radius:999px;' +
    '}' +
    '.p-cursor-dot{' +
      'width:6px;height:6px;margin:-3px 0 0 -3px;' +
      'background:#1A1512;' +
      'transition:opacity .22s cubic-bezier(.22,.61,.36,1),' +
      'width .22s cubic-bezier(.22,.61,.36,1),' +
      'height .22s cubic-bezier(.22,.61,.36,1),' +
      'background .22s cubic-bezier(.22,.61,.36,1);' +
    '}' +
    '.p-cursor-ring{' +
      'width:18px;height:18px;margin:-9px 0 0 -9px;' +
      'border:1.5px solid rgba(26,21,18,0.0);' +
      'transition:opacity .28s cubic-bezier(.22,.61,.36,1),' +
      'width .28s cubic-bezier(.22,.61,.36,1),' +
      'height .28s cubic-bezier(.22,.61,.36,1),' +
      'border-color .28s cubic-bezier(.22,.61,.36,1);' +
    '}' +
    /* over interactive */
    '.p-cursor-ring.is-active{' +
      'width:34px;height:34px;margin:-17px 0 0 -17px;' +
      'border-color:rgba(232,197,71,0.85);' +
    '}' +
    '.p-cursor-dot.is-active{width:3px;height:3px;margin:-1.5px 0 0 -1.5px;background:#E8C547}' +
    /* over patch grid */
    '.p-cursor-ring.is-grid{' +
      'width:22px;height:22px;margin:-11px 0 0 -11px;' +
      'border-color:rgba(26,21,18,0.45);' +
    '}' +
    /* hidden over media */
    '.p-cursor-dot.is-hidden,.p-cursor-ring.is-hidden{opacity:0}' +
    /* dark sections — flip dot/ring color */
    '.section--dark .p-cursor-dot,footer .p-cursor-dot{background:#F5EFE4}';

  var styleEl = document.createElement('style');
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  // ── Create elements ─────────────────────────────────────────────────
  var dot  = document.createElement('div'); dot.className  = 'p-cursor-dot';
  var ring = document.createElement('div'); ring.className = 'p-cursor-ring';
  document.body.appendChild(dot);
  document.body.appendChild(ring);

  // ── Movement (rAF + lag on ring) ────────────────────────────────────
  var mx = window.innerWidth / 2, my = window.innerHeight / 2;
  var rx = mx, ry = my;
  var raf = null;

  function frame() {
    // Lag the ring slightly for elegance
    rx += (mx - rx) * 0.22;
    ry += (my - ry) * 0.22;
    dot.style.transform  = 'translate3d(' + mx + 'px,' + my + 'px,0)';
    ring.style.transform = 'translate3d(' + rx + 'px,' + ry + 'px,0)';
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  document.addEventListener('mousemove', function (e) {
    mx = e.clientX; my = e.clientY;
  }, { passive: true });

  // Hide when leaving window
  document.addEventListener('mouseleave', function () {
    dot.classList.add('is-hidden');
    ring.classList.add('is-hidden');
  });
  document.addEventListener('mouseenter', function () {
    dot.classList.remove('is-hidden');
    ring.classList.remove('is-hidden');
  });

  // ── Context detection (interactive / grid / media) ──────────────────
  var INTERACTIVE = 'a,button,[role="button"],[data-pcr-card],[data-pcr-filter],.button,.patch-card,.canvas-card,.pcr-tray-btn,.patchistry-sound-toggle,input[type="submit"],input[type="button"],summary';
  var MEDIA       = 'video,iframe';
  var GRID        = '[data-pcr-grid],.pcr-grid,.patch-grid';

  document.addEventListener('mouseover', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest(MEDIA)) {
      dot.classList.add('is-hidden'); ring.classList.add('is-hidden');
      return;
    }
    dot.classList.remove('is-hidden'); ring.classList.remove('is-hidden');
    var isInteractive = !!t.closest(INTERACTIVE);
    var isGrid        = !!t.closest(GRID);
    dot.classList.toggle('is-active', isInteractive);
    ring.classList.toggle('is-active', isInteractive);
    ring.classList.toggle('is-grid', isGrid && !isInteractive);
  }, { passive: true });

  // ── Cleanup if needed ───────────────────────────────────────────────
  window.addEventListener('pagehide', function () {
    if (raf) cancelAnimationFrame(raf);
  });
})();
